/**
 * Back from the review page to the lab desk (PROD-3204, Andrew 23 Oct, conference demo): Order Requests → Review values →
 * Back (the bar's Back or the browser's) lands on Order Requests on the same page, filters and scroll, not the Calendar.
 *
 * Order Requests is a tab of the calendar (`/`) held in component state, so the address alone reopened the Calendar.
 * Before the review opens, the desk writes its state into the current address (replace, so the browser's Back returns to
 * it) and gives the review that address as `from`. Behind `intake-desk`: only the desk variant reads or writes these.
 *
 * Params on `/`: `tab=order-requests`, `orPage`, `orSize`, `orFilters` (JSON of the wire filters and their chip labels),
 * `orScroll` (the list's scroll top in px). The calendar keeps every param it does not own (scheduling-page.tsx).
 */
import type { OrderRequestsFilters } from '@/scheduling/components/order-requests/order-requests-filter-panel';
import { LAB_ORDER_STATUS, type LabOrderStatus } from '@/types/scheduling-api';

export const ORDER_REQUESTS_TAB_PARAM = 'tab';
export const ORDER_REQUESTS_TAB_VALUE = 'order-requests';
const PAGE = 'orPage';
const SIZE = 'orSize';
const FILTERS = 'orFilters';
const SCROLL = 'orScroll';

export interface OrderRequestsDeskState {
  pageNumber: number;
  pageSize: number;
  filters: OrderRequestsFilters;
  scrollTop: number;
}

const FILTER_KEYS = ['patientId', 'patientName', 'mrnLabel', 'fromDate', 'toDate', 'labVendorId'] as const;
const STATUSES = new Set<string>(Object.values(LAB_ORDER_STATUS));

/** The address says the calendar should open on Order Requests. */
export const opensOrderRequests = (search: string) =>
  new URLSearchParams(search).get(ORDER_REQUESTS_TAB_PARAM) === ORDER_REQUESTS_TAB_VALUE;

/**
 * The tab the scheduling frame opens on: a confined role's own tab; else Order Requests when the address asks for it
 * (Back from the lab desk's review, `intake-desk` on); else the Calendar.
 */
export function initialSchedulingTab<T extends string>(confinedTab: T | null, search: string, deskOn: boolean): T | 'orderRequests' | 'calendar' {
  if (confinedTab) return confinedTab;
  return deskOn && opensOrderRequests(search) ? 'orderRequests' : 'calendar';
}

/** The desk's state, as far as the address carries it; anything unreadable is left at its default. */
export function readOrderRequestsState(search: string, pageSizes: readonly number[]): Partial<OrderRequestsDeskState> {
  const p = new URLSearchParams(search);
  if (p.get(ORDER_REQUESTS_TAB_PARAM) !== ORDER_REQUESTS_TAB_VALUE) return {};
  const out: Partial<OrderRequestsDeskState> = {};
  const page = Number(p.get(PAGE));
  if (Number.isInteger(page) && page >= 1) out.pageNumber = page;
  const size = Number(p.get(SIZE));
  if (pageSizes.includes(size)) out.pageSize = size;
  const scroll = Number(p.get(SCROLL));
  if (Number.isFinite(scroll) && scroll > 0) out.scrollTop = Math.round(scroll);
  const raw = p.get(FILTERS);
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const f: OrderRequestsFilters = {};
        const src = parsed as Record<string, unknown>;
        for (const k of FILTER_KEYS) if (typeof src[k] === 'string' && src[k]) f[k] = src[k] as string;
        if (typeof src.status === 'string' && STATUSES.has(src.status)) f.status = src.status as LabOrderStatus;
        out.filters = f;
      }
    } catch {
      // A hand-edited or cut address: the filters start empty.
    }
  }
  return out;
}

/** The current address with the desk's state written in (the other params, the calendar's own, are kept). */
export function writeOrderRequestsState(pathname: string, search: string, state: OrderRequestsDeskState): string {
  const p = new URLSearchParams(search);
  p.set(ORDER_REQUESTS_TAB_PARAM, ORDER_REQUESTS_TAB_VALUE);
  p.set(PAGE, String(state.pageNumber));
  p.set(SIZE, String(state.pageSize));
  const filters = Object.fromEntries(Object.entries(state.filters).filter(([, v]) => v !== undefined && v !== ''));
  if (Object.keys(filters).length > 0) p.set(FILTERS, JSON.stringify(filters));
  else p.delete(FILTERS);
  if (state.scrollTop > 0) p.set(SCROLL, String(Math.round(state.scrollTop)));
  else p.delete(SCROLL);
  return `${pathname}?${p.toString()}`;
}

/**
 * The return address a page was given, only when it is the lab desk's own address: the calendar (`/`) on this origin.
 * It is parsed as the browser would read it (Scrips Agent, #1376: `/\t/evil.com` drops the tab and becomes
 * `//evil.com`), and only the path and query of the parsed address are returned, never the text as given.
 */
export function safeReturnPath(from: string | null | undefined): string | null {
  if (!from || !from.startsWith('/')) return null;
  const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  let url: URL;
  try {
    url = new URL(from, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || url.pathname !== '/') return null;
  return `${url.pathname}${url.search}`;
}

/**
 * History state the desk puts on the review it opens: the entry just below is the desk's own (with its state), so the
 * review can step back to it instead of adding the desk again. Change patient opens a review without it.
 */
export const DESK_ENTRY_BELOW = { deskEntryBelow: true } as const;
export const isDeskEntryBelow = (state: unknown) =>
  !!state && typeof state === 'object' && (state as { deskEntryBelow?: unknown }).deskEntryBelow === true;

/** `path` with `from` added, for the page that is opened. */
export function withReturnPath(path: string, from: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}from=${encodeURIComponent(from)}`;
}
